import { useMemo, useState } from "react";
import type { PairSimProfile } from "@opus-dt/shared-types";
import { Notify, toast } from "@/components/ui/notify";
import type { MachineDetail } from "@/features/lines/hooks";
import { useProducts } from "@/features/products/hooks";
import { useMachineSidebarBridge } from "../hooks/useMachineSidebarBridge";
import type { SidebarMode } from "../MachineSidebar";
import { AddCompatibilityForm } from "./AddCompatibilityForm";
import { CompatibilityItem } from "./CompatibilityItem";

interface Props {
  machine: MachineDetail;
  lineId: string;
  mode: SidebarMode;
  onDirtyChange?: (dirty: boolean) => void;
}

export function CompatibilitiesTab({ machine, lineId, mode, onDirtyChange }: Props) {
  const bridge = useMachineSidebarBridge(machine.id, lineId);
  const canEdit = mode !== "view" && !bridge.readOnly;
  const productsQuery = useProducts();
  const { data: products = [] } = productsQuery;
  const [pending, setPending] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState("");

  const machineCompat = bridge.compatibilities;
  const linkedProductIds = useMemo(
    () => new Set(machineCompat.map((c) => c.productId)),
    [machineCompat]
  );
  const availableProducts = useMemo(
    () => products.filter((p) => !linkedProductIds.has(p.id)),
    [products, linkedProductIds]
  );

  // O toast amigável de FALHA vem do MutationCache global; aqui só o caminho feliz.
  async function run(action: () => Promise<void>, okMessage: string) {
    onDirtyChange?.(true);
    setPending(true);
    try {
      await action();
      toast.success(okMessage);
      onDirtyChange?.(false);
    } catch {
      // erro já reportado (toast global em edit, toast local do limite em rascunho)
    } finally {
      setPending(false);
    }
  }

  async function removeCompat(productId: string, productName: string) {
    const ok = await Notify.confirm({
      variant: "warning",
      title: "Remover esta compatibilidade?",
      description: `A máquina deixa de aceitar ${productName} nas simulações e no planejamento.`,
      confirmLabel: "Remover",
    });
    if (!ok) return;
    await run(() => bridge.setCompatibility(productId, false), "Compatibilidade removida.");
  }

  const nameOf = (productId: string, fallback: string) =>
    fallback || products.find((p) => p.id === productId)?.name || productId;

  if (!lineId) {
    return (
      <p className="text-sm text-muted-foreground">
        Salve a máquina primeiro para configurar compatibilidades.
      </p>
    );
  }

  if (bridge.compatibilitiesIsError) {
    return (
      <Notify.QueryError
        error={bridge.compatibilitiesError}
        onRetry={bridge.refetchCompatibilities}
        variant="inline"
      />
    );
  }

  return (
    <div className="space-y-3">
      {machineCompat.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhum produto compatível cadastrado.</p>
      ) : (
        <ul className="space-y-1.5">
          {machineCompat.map((c) => {
            const productName = nameOf(c.productId, c.product.name);
            return (
              <CompatibilityItem
                key={c.id}
                compat={c}
                productName={productName}
                canEdit={canEdit}
                pending={pending}
                onRemove={() => void removeCompat(c.productId, productName)}
                onSaveProfile={(next: PairSimProfile | null) =>
                  void run(
                    () => bridge.setCompatibility(c.productId, c.enabled, next),
                    next ? "Perfil do produto salvo." : "Perfil do produto removido."
                  )
                }
              />
            );
          })}
        </ul>
      )}

      {canEdit ? (
        <AddCompatibilityForm
          available={availableProducts}
          value={selectedProductId}
          onChange={setSelectedProductId}
          onAdd={() =>
            void run(async () => {
              await bridge.setCompatibility(selectedProductId, true);
              setSelectedProductId("");
            }, "Compatibilidade adicionada.")
          }
          pending={pending}
          isError={productsQuery.isError}
          error={productsQuery.error}
          onRetry={() => void productsQuery.refetch()}
        />
      ) : null}
    </div>
  );
}
